import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { COLOUR_THEME_STORAGE_KEY } from '@/constants/localStorageKeys';

type ColourTheme = 'bright' | 'dark' | 'soft';

type ColourThemeContextValue = {
  theme: ColourTheme;
  setTheme: (theme: ColourTheme) => void;
};

const ColourThemeContext = createContext<ColourThemeContextValue | undefined>(undefined);

export const ColourThemeProvider = ({ children }: PropsWithChildren) => {
  const hasAppliedThemeRef = useRef(false);

  const [theme, setTheme] = useState<ColourTheme>(() => {
    try {
      const storedTheme = window.localStorage.getItem(COLOUR_THEME_STORAGE_KEY) as ColourTheme | null;
      if (storedTheme === 'bright' || storedTheme === 'dark' || storedTheme === 'soft') {
        return storedTheme;
      }
    } catch {
      // Access to localStorage can fail (e.g. privacy mode); fall back to default theme.
    }
    return 'bright';
  });

  // Apply selected theme as a body class and persist it.
  useEffect(() => {
    const themeClassMap: Record<ColourTheme, string> = {
      bright: 'theme-bright',
      dark: 'theme-dark',
      soft: 'theme-soft',
    };

    const currentClass = themeClassMap[theme];
    const shouldAnimateThemeChange = hasAppliedThemeRef.current;
    let transitionTimeoutId: number | undefined;

    if (shouldAnimateThemeChange) {
      document.body.classList.add('theme-transitioning');
      transitionTimeoutId = window.setTimeout(() => {
        document.body.classList.remove('theme-transitioning');
      }, 220);
    }

    document.body.classList.remove('theme-bright', 'theme-dark', 'theme-soft');
    document.body.classList.add(currentClass);
    hasAppliedThemeRef.current = true;

    try {
      window.localStorage.setItem(COLOUR_THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore persistence failures – the UI will still update for the current session.
    }

    return () => {
      if (transitionTimeoutId !== undefined) {
        window.clearTimeout(transitionTimeoutId);
        document.body.classList.remove('theme-transitioning');
      }
    };
  }, [theme]);

  const value = useMemo<ColourThemeContextValue>(
    () => ({
      theme,
      setTheme,
    }),
    [theme],
  );

  return <ColourThemeContext.Provider value={value}>{children}</ColourThemeContext.Provider>;
};

export const useColourTheme = (): ColourThemeContextValue => {
  const context = useContext(ColourThemeContext);

  if (!context) {
    throw new Error('useColourTheme must be used within a ColourThemeProvider');
  }

  return context;
};
